import { createWhatsappFlowNode, normalizeWhatsappStepData } from "./whatsappFlowStorage";
import { normalizeStepsEditionEntry } from "./whatsappFlowStepsEdition";

/** Separación horizontal entre el paso más a la derecha del flujo actual y los pasos copiados. */
export const COPY_X_GAP = 400;

let copyBatchCounter = 0;

/**
 * Agrega al flujo actual los pasos (y sus conexiones internas) de otro flujo.
 * - Los pasos copiados reciben ids nuevos para no chocar con los existentes.
 * - Las referencias internas (aristas, `stepsEdition`, `autoOptionBranch`) se remapean.
 * - No se crea ninguna conexión entre los pasos actuales y los copiados.
 * - Los copiados se ubican a la derecha del paso con mayor `x`, conservando su disposición.
 *
 * @param {{ currentNodes: object[], currentEdges: object[], sourceNodes: object[], sourceEdges: object[] }} params
 * @returns {{ nodes: object[], edges: object[], firstPastedId: string|null, pastedCount: number }}
 */
export const appendCopiedSteps = ({
  currentNodes = [],
  currentEdges = [],
  sourceNodes = [],
  sourceEdges = [],
}) => {
  copyBatchCounter += 1;
  const batch = `${Date.now()}-${copyBatchCounter}`;
  const usedIds = new Set(currentNodes.map((node) => String(node.id)));

  const idMap = {};
  sourceNodes.forEach((node, idx) => {
    let candidate = `step-copy-${batch}-${idx + 1}`;
    while (usedIds.has(candidate)) candidate = `${candidate}-x`;
    usedIds.add(candidate);
    idMap[String(node.id)] = candidate;
  });

  const currentMaxX = currentNodes.reduce(
    (acc, node) => Math.max(acc, Number(node.position?.x) || 0),
    -Infinity
  );
  const sourceMinX = sourceNodes.reduce(
    (acc, node) => Math.min(acc, Number(node.position?.x) || 0),
    Infinity
  );
  const offsetX =
    currentNodes.length > 0 && sourceNodes.length > 0
      ? currentMaxX + COPY_X_GAP - sourceMinX
      : 0;

  const pastedNodes = sourceNodes.map((node) => {
    const data = node.data && typeof node.data === "object" ? { ...node.data } : {};

    if (Array.isArray(data.stepsEdition)) {
      data.stepsEdition = data.stepsEdition
        .map((entry) => {
          const target = idMap[String(entry?.nodeId)];
          if (!target) return null;
          return normalizeStepsEditionEntry({ nodeId: target, label: entry?.label });
        })
        .filter(Boolean);
    }

    if (data.autoOptionBranch && typeof data.autoOptionBranch === "object") {
      const parent = idMap[String(data.autoOptionBranch.parentId)];
      if (parent) {
        data.autoOptionBranch = { ...data.autoOptionBranch, parentId: parent };
      } else {
        delete data.autoOptionBranch;
      }
    }

    const position = node.position || { x: 0, y: 0 };
    return {
      ...node,
      id: idMap[String(node.id)],
      position: { x: (Number(position.x) || 0) + offsetX, y: Number(position.y) || 0 },
      selected: false,
      data,
    };
  });

  const pastedEdges = sourceEdges
    .filter((edge) => idMap[String(edge.source)] && idMap[String(edge.target)])
    .map((edge) => {
      const source = idMap[String(edge.source)];
      const target = idMap[String(edge.target)];
      const handlePart = edge.sourceHandle ? `-${edge.sourceHandle}` : "";
      return {
        ...edge,
        id: `e-${source}${handlePart}-${target}`,
        source,
        target,
        selected: false,
      };
    });

  return {
    nodes: [...currentNodes, ...pastedNodes],
    edges: [...currentEdges, ...pastedEdges],
    firstPastedId: pastedNodes[0]?.id ?? null,
    pastedCount: pastedNodes.length,
  };
};

const pristineStepData = () =>
  JSON.stringify(createWhatsappFlowNode("step-1", 1, { x: 0, y: 0 }).data);

/**
 * Indica si el editor sigue en el estado inicial de un flujo nuevo: un único "Paso 1"
 * sin modificar y sin conexiones.
 *
 * @param {object[]} nodes
 * @param {object[]} edges
 * @returns {boolean}
 */
export const isPristineInitialFlow = (nodes, edges) => {
  if (!Array.isArray(nodes) || nodes.length !== 1) return false;
  if (Array.isArray(edges) && edges.length > 0) return false;
  const [node] = nodes;
  if (node.id !== "step-1") return false;
  return JSON.stringify(normalizeWhatsappStepData(node.data)) === pristineStepData();
};
